/**
 * 「서브 관리자로 접속」 화면 규칙(W6 B2·B3, 계약 SUPPORT.md 5·9 — 리셀러 서브 관리자).
 * 진입: POST /support/sessions → 201 {code, app, expiresInSec:60} → 새 탭 <앱 주소>/support/enter#c=<code>.
 * 403 SUPPORT_PROOF_REQUIRED(하루 첫 진입) → 본인 확인 → POST /support/proof → 한 번만 다시.
 *   2FA 가 있으면 기존 step-up(challenge → verify)으로 받은 mfaToken 을, 없으면 본인 비밀번호를 보낸다(계약 고침 20de33c3d).
 *   프로필 mfa.supportProofRequired 가 false(2차 인증을 안 쓰는 사람)면 확인 창 없이 바로 다시 보낸다(T-CONTRACT-1031 §1).
 * 403 SUPPORT_ACCESS_OFF(지원 진입이 아직 꺼짐) → 그때만 「이전 방식으로 열기」(옛 대리 토큰, 정리 배포에서 삭제).
 * Vue·axios 를 들이지 않는 순수 모듈이라 node --test 로 시험한다(tests/supportEntry.test.mjs).
 */

import { errorCode, isLegacyBlocked } from './standardErrors.js';
import { isStepUpCancelled } from './mfa.js';

export { isLegacyBlocked };
const statusOf = (error) => error?.response?.status;

/** 진입 사유 코드(계약 SUPPORT_REASON_CODES). 기본값은 고객 문의 */
export const SUPPORT_REASONS = [
  { value: 'CUSTOMER_REQUEST', label: '고객 문의' },
  { value: 'ONBOARDING', label: '초기 설정' },
  { value: 'INCIDENT', label: '장애' },
  { value: 'SECURITY', label: '보안' },
  { value: 'OTHER', label: '기타' },
];
export const DEFAULT_REASON = 'CUSTOMER_REQUEST';

const STUDIO = { value: 'studio', label: '스튜디오' };
const APPS_BY_TYPE = {
  HOSPITAL: [STUDIO, { value: 'admin-hospital', label: '병원 관리자 페이지' }],
  MANUFACTURER: [
    STUDIO,
    { value: 'admin-manufacturer', label: '제조사 관리자 페이지' },
  ],
  DENTAL_LABORATORY: [
    STUDIO,
    { value: 'admin-manufacturer', label: '제조사 관리자 페이지' },
  ],
  STUDIO: [STUDIO],
};

/** 회사 종류 → 들어갈 수 있는 앱(계약 SUPPORT_APPS). 모르는 종류는 스튜디오만 */
export const appsFor = (companyType) => APPS_BY_TYPE[companyType] || [STUDIO];

/** 앱 → 주소 env(CI-06). 값이 비면 null */
const APP_URL_ENV = {
  studio: 'VUE_APP_STUDIO_URL',
  'admin-hospital': 'VUE_APP_HOSPITAL_ADMIN_URL',
  'admin-manufacturer': 'VUE_APP_MANUFACTURER_ADMIN_URL',
};

/** 진입 주소 `<앱>/support/enter#c=<code>`. 코드는 주소 조각(#)에만 — 서버 로그·Referer 에 남지 않게 */
export const enterUrl = (app, code, env = {}) => {
  const base = String(env[APP_URL_ENV[app]] || '').replace(/\/+$/, '');
  if (!base || !code) return null;
  return `${base}/support/enter#c=${encodeURIComponent(code)}`;
};

/** 사유 검사: 괜찮으면 "", 아니면 안내 문구(OTHER 는 메모 필수) */
export const reasonError = (reasonCode, reasonNote) => {
  if (!SUPPORT_REASONS.some((r) => r.value === reasonCode))
    return '사유를 고르세요.';
  if (reasonCode === 'OTHER' && !String(reasonNote ?? '').trim())
    return '기타 사유는 메모를 적어 주세요.';
  return '';
};

/** POST /support/sessions 본문. 메모는 있을 때만 */
export const sessionBody = (companyId, app, reasonCode, reasonNote) => {
  const note = String(reasonNote ?? '').trim();
  return { companyId, app, reasonCode, ...(note ? { reasonNote: note } : {}) };
};

/** 본인 확인 증표는 기존 step-up 으로 받는다(LOGIN 목적, 2FA 코드는 고치지 않음) */
export const PROOF_STEP_UP = {
  challenge: '/auth/2fa/step-up/challenge',
  verify: '/auth/2fa/step-up/verify',
  purpose: 'LOGIN',
};

/**
 * 본인 확인을 건너뛸 사람인가(T-CONTRACT-1031 §1): 프로필 mfa.supportProofRequired 가 false(2차 인증을 쓰지 않음).
 * 값이 없으면(옛 inapi) false — 지금처럼 서버가 요구하면 확인 창.
 */
export const proofSkippable = (mfa) => mfa?.supportProofRequired === false;

/**
 * sendWithProof 의 prove 자리: 건너뛸 사람이면 창 없이 true(바로 다시 보냄), 아니면 ask()(확인 창).
 * 프로필을 못 읽으면 ask() — 확인 창이 프로필을 다시 읽어 안내한다.
 * 프로필을 읽는 사이 창을 닫았으면(alive() false) 확인 창으로 넘어가지 않고 false(취소).
 * @param getProfile () => Promise<GET /auth/profile 본문>
 * @param ask () => Promise<boolean>
 * @param alive () => 이 흐름이 아직 살아 있는가(창을 닫지 않음)
 */
export async function proveUnlessSkippable(
  getProfile,
  ask,
  alive = () => true
) {
  let profile = null;
  try {
    profile = await getProfile();
  } catch {
    // 아래 확인 창으로
  }
  if (!alive()) return false;
  if (proofSkippable(profile?.mfa)) return true;
  return ask();
}

/**
 * 「접속」·「켜기/끄기」 한 번을 창에 묶는다(진입 창·동의 스위치가 같이 씀).
 * run(): loading 을 켜고 요청 + 본인 확인(sendWithProof) → done(data)·fail(error) → loading 끔.
 * cancel(): 창을 닫음 — 진행 중 흐름을 버리고(늦게 온 프로필·응답은 아무것도 바꾸지 않음) loading 을 바로 끈다.
 * 다시 열어 누르면 새 흐름이라 앞 흐름이 loading·결과를 건드리지 않는다.
 * @param setLoading (on) => void
 */
export function createProofRunner(setLoading) {
  let current = 0;
  return {
    async run({ post, body, getProfile, ask, done, fail }) {
      const id = ++current;
      const alive = () => id === current;
      setLoading(true);
      try {
        const data = await sendWithProof(post, body, () =>
          proveUnlessSkippable(getProfile, ask, alive)
        );
        if (alive()) await done(data);
      } catch (error) {
        if (alive()) fail(error);
      } finally {
        if (alive()) setLoading(false);
      }
    },
    cancel() {
      current += 1;
      setLoading(false);
    },
  };
}

/** 본인 확인 방법: 휴대폰·이메일 수단이 있으면 step-up 증표(mfa), 없으면 본인 비밀번호(리셀러 2FA 없을 때만) */
export const proofMethodOf = (factorMethods = []) =>
  factorMethods.length ? 'mfa' : 'password';

/** POST /support/proof 본문 */
export const proofBody = (method, value) =>
  method === 'mfa'
    ? { method: 'mfa', mfaToken: value }
    : { method: 'password', password: value };

/** POST /support/proof 거절 이유(403 SUPPORT_PROOF_REQUIRED + reason, inapi support-proof.service) → 문구(admin-certilife 와 같은 글자) */
export const PROOF_REASON_MESSAGES = {
  MFA_EXEMPT:
    '본인 확인은 2차 인증 수단이 필요합니다(2차 인증 예외로는 확인할 수 없음). 「내 계정 보안」에서 수단을 등록하세요.',
  MFA_STALE: '인증한 지 오래되었습니다. 인증번호를 다시 받아 확인하세요.',
  MFA_TOKEN_INVALID:
    '본인 확인이 맞지 않습니다. 인증번호를 다시 받아 확인하세요.',
  PASSWORD_MISMATCH: '비밀번호가 맞지 않습니다.',
  PASSWORD_NOT_ALLOWED: '2차 인증 수단이 있는 계정은 인증번호로 확인하세요.',
};
// 이유를 듣고 화면이 할 일: 비밀번호 칸 비우기 · 프로필을 다시 읽어 인증번호 창으로
const PROOF_REASON_ACTIONS = {
  PASSWORD_MISMATCH: 'CLEAR_PASSWORD',
  PASSWORD_NOT_ALLOWED: 'SWITCH_TO_MFA',
};

/**
 * POST /support/proof 실패 → {text, action?}. 이유가 없거나 모르는 이유면 MFA_EXEMPT 문구.
 * 403 SUPPORT_PROOF_REQUIRED 가 아니면 서버 문구(없으면 고정 문구).
 */
export const proofFailure = (error) => {
  if (
    statusOf(error) === 403 &&
    errorCode(error) === 'SUPPORT_PROOF_REQUIRED'
  ) {
    const reason = error?.response?.data?.reason;
    return {
      text: PROOF_REASON_MESSAGES[reason] || PROOF_REASON_MESSAGES.MFA_EXEMPT,
      action: PROOF_REASON_ACTIONS[reason],
    };
  }
  const server = error?.response?.data?.message;
  return {
    text:
      typeof server === 'string' && server
        ? server
        : '본인 확인을 하지 못했습니다. 다시 시도하세요.',
  };
};

export const ENTRY_GUIDE =
  '새 탭에서 열립니다. 15분마다 이어지고 30분 쉬거나 8시간이면 끝납니다.';
export const actorGuide = (userName, companyName) =>
  `이 접속에서 한 일은 ${userName || '로그인한 사람'}(리셀러 ${
    companyName || '-'
  }) 이름으로 기록됩니다.`;

export const ENTRY_MESSAGES = {
  ACCESS_OFF:
    '지원 접속이 아직 켜지지 않았습니다. 지금은 이전 방식으로 열 수 있습니다.',
  UNLINKED_OR_LOCKED: '이 고객사와 연동이 끊겼거나 지원 계정이 잠겼습니다.',
  CONSENT_REQUIRED:
    '이 회사는 「고객 지원을 위한 접근 허용」이 꺼져 있습니다. 고객 상세의 스위치로 먼저 켜 주세요.',
  MFA_ENROLL_REQUIRED:
    '이 고객사는 2차 인증 필수입니다. 「내 계정 보안」에서 수단을 먼저 등록하세요.',
  AUDIT_UNAVAILABLE:
    '기록 장치 문제로 이 작업을 하지 않았습니다. 잠시 후 다시 시도해 주세요.',
  FAILED: '접속하지 못했습니다. 잠시 뒤 다시 시도하세요.',
  PROFILE_FAILED:
    '내 계정 정보를 불러오지 못했습니다. 잠시 뒤 다시 시도하세요.',
  PROOF_NOT_APPLIED:
    '본인 확인이 반영되지 않았습니다. 창을 닫고 다시 시도하세요.',
  POPUP_BLOCKED:
    '브라우저가 새 탭을 막았습니다. 아래 「새 탭 열기」를 눌러 주세요(60초 안).',
  NO_APP_URL:
    '이 앱의 주소가 설정되지 않았습니다. 서티라이프 운영팀에 알려 주세요.',
};

/**
 * POST /support/sessions 실패 → 다음 동작.
 *  PROOF  본인 확인 후 한 번 다시
 *  LEGACY 「이전 방식으로 열기」를 보임(supportAccess=off 일 때만)
 *  ERROR  문구(+ link 'MY_SECURITY' 면 「내 계정 보안」 링크)
 */
export const entryOutcome = (error) => {
  const status = statusOf(error);
  const code = errorCode(error);
  if (status === 403 && code === 'SUPPORT_PROOF_REQUIRED')
    return { kind: 'PROOF' };
  if (status === 403 && code === 'SUPPORT_ACCESS_OFF')
    return { kind: 'LEGACY', text: ENTRY_MESSAGES.ACCESS_OFF };
  if (status === 403 && code === 'MFA_ENROLL_REQUIRED')
    return {
      kind: 'ERROR',
      text: ENTRY_MESSAGES.MFA_ENROLL_REQUIRED,
      link: 'MY_SECURITY',
    };
  if (
    status === 403 &&
    (code === 'PERMISSION_DENIED' || code === 'SUPPORT_SUB_ACCOUNT_LOCKED')
  )
    return { kind: 'ERROR', text: ENTRY_MESSAGES.UNLINKED_OR_LOCKED };
  if (status === 403 && code === 'SUPPORT_CONSENT_REQUIRED')
    return { kind: 'ERROR', text: ENTRY_MESSAGES.CONSENT_REQUIRED };
  if (status === 503 && code === 'AUDIT_UNAVAILABLE')
    return { kind: 'ERROR', text: ENTRY_MESSAGES.AUDIT_UNAVAILABLE };
  const server = error?.response?.data?.message;
  return {
    kind: 'ERROR',
    text: typeof server === 'string' && server ? server : ENTRY_MESSAGES.FAILED,
  };
};

/**
 * 진입 실패 → 창에 보일 결과. 사용자가 본인 확인·step-up 창을 닫았으면 null(조용히 멈춤).
 * 본인 확인 뒤 한 번 다시 보낸 요청이 또 SUPPORT_PROOF_REQUIRED 면 「반영되지 않음」(재시도는 하지 않음).
 */
export const entryResult = (error) => {
  if (error?.proofCancelled || isStepUpCancelled(error)) return null;
  const outcome = entryOutcome(error);
  return outcome.kind === 'PROOF'
    ? { kind: 'ERROR', text: ENTRY_MESSAGES.PROOF_NOT_APPLIED }
    : outcome;
};

/**
 * 요청 + 본인 확인 한 번(진입 POST /support/sessions · 동의 PUT 이 같이 씀). 403 SUPPORT_PROOF_REQUIRED 면
 * prove() 가 true(확인 끝)일 때 한 번만 다시 보낸다.
 * prove() 가 false(창을 닫음)거나 두 번째도 실패하면 그 오류를 던진다(같은 확인을 되풀이하지 않음).
 * @param post (body) => Promise<{data}>
 * @param prove () => Promise<boolean>
 */
export async function sendWithProof(post, body, prove) {
  try {
    return (await post(body)).data;
  } catch (error) {
    if (entryOutcome(error).kind !== 'PROOF') throw error;
    if (!(await prove())) {
      error.proofCancelled = true;
      throw error;
    }
    return (await post(body)).data;
  }
}
export const startSupportSession = sendWithProof;

/**
 * 직원 목록 줄이 지원 서브 계정(「서티라이프 지원」·「○○ 지원」, 아이디 support-certilife-·support-reseller-)인지.
 * 서버가 목록 응답 줄에 support:{kind,label} 를 단다(계약 SUPPORT 3·4). 판정은 이 칸으로만(admin-hospital·manufacturer 와 같음).
 * 서버도 편집·삭제를 거절(SUPPORT_ACCOUNT_READONLY) — 화면 숨김은 편의.
 */
export const isSupportRow = (row) => !!row?.support && typeof row.support === 'object';

/**
 * 「고객 지원을 위한 접근 허용」(동의 하나, 서티라이프·리셀러 지원 접속 모두에 적용 — CONSENT-1028).
 * GET/PUT /support/companies/:companyId/consent. 이름·설명은 서버 text 가 먼저, 이 값은 없을 때만.
 */
export const CONSENT_TEXT = {
  title: '고객 지원을 위한 접근 허용',
  summary:
    '고객 지원을 위해 서티라이프·담당 리셀러 담당자가 우리 회사 화면에 접근하는 것을 허용하거나 끕니다.',
};
// 고객사에는 메일을 보내지 않음(11-03 사용자) — 고객사 이용 내역에만 남음
export const CONSENT_MESSAGES = {
  ON: '켰어요.',
  OFF: '껐어요.',
  ENTRY_HINT: '먼저 고객 지원을 위한 접근 허용을 켜 주세요',
  LOAD_FAILED: '상태를 불러오지 못했어요.',
  REASON_REQUIRED: '사유를 적어 주세요.',
  REASON_TOO_LONG: '사유는 200자까지 적을 수 있어요.',
  FAILED: '바꾸지 못했습니다. 잠시 뒤 다시 시도하세요.',
};
export const CONSENT_REASON_MAX = 200;

export const consentUrl = (companyId) =>
  `/support/companies/${encodeURIComponent(companyId)}/consent`;

/** 켠 쪽 문구(화면 공통 표). 꺼짐: 새 회사가 아직 안 고름 · 끈 때가 있으면 「○○가 껐어요」 */
export const consentSourceText = (consent) => {
  if (!consent) return '';
  const name = consent.consentedBy?.name;
  if (consent.status !== 'ON') {
    if (consent.needsChoice) return '고객사 관리자가 아직 고르지 않았어요';
    // 끈 쪽: 서버 revokedBy(관리자 이름 또는 서티라이프·리셀러 회사 이름) + revokeSource
    if (consent.revokedAt) {
      const by = consent.revokedBy?.name;
      if (!by) return '관리자가 껐어요';
      return consent.revokeSource && consent.revokeSource !== 'IN_APP'
        ? `${by}에서 껐어요`
        : `${by} 님이 껐어요`;
    }
    return '';
  }
  switch (consent.source) {
    case 'EXISTING':
      return '기존 가입 회사라 기본으로 켜져 있어요';
    case 'IN_APP':
      return `${name || '고객사 관리자'} 님이 켰어요`;
    case 'OPERATOR':
    case 'RESELLER':
      return `${name || '-'}에서 유지보수 계약으로 켰어요`;
    case 'OFFLINE':
      return '서면 동의로 켜졌어요';
    default:
      return '';
  }
};

/** 사유 검사: 괜찮으면 "", 아니면 안내 문구(필수, 200자) */
export const consentReasonError = (reason) => {
  const text = String(reason ?? '').trim();
  if (!text) return CONSENT_MESSAGES.REASON_REQUIRED;
  if (text.length > CONSENT_REASON_MAX) return CONSENT_MESSAGES.REASON_TOO_LONG;
  return '';
};

/** PUT /support/companies/:companyId/consent 본문 */
export const consentBody = (status, reason) => ({
  status,
  reason: String(reason ?? '').trim(),
});

/**
 * 동의 PUT 실패 → 창에 보일 문구. 본인 확인 창을 닫았으면 ""(조용히 사유 창으로).
 * 본인 확인 뒤에도 또 확인 요구면 「반영되지 않음」, 그 밖엔 서버 문구(없으면 고정 문구).
 */
export const consentError = (error) => {
  if (error?.proofCancelled || isStepUpCancelled(error)) return '';
  if (entryOutcome(error).kind === 'PROOF')
    return ENTRY_MESSAGES.PROOF_NOT_APPLIED;
  if (statusOf(error) === 503 && errorCode(error) === 'AUDIT_UNAVAILABLE')
    return ENTRY_MESSAGES.AUDIT_UNAVAILABLE;
  const server = error?.response?.data?.message;
  return typeof server === 'string' && server
    ? server
    : CONSENT_MESSAGES.FAILED;
};

/** 옛 대리 길(리셀러 대리 토큰·음성봇 SSO·리셀러 대행) 막힘 문구 — 403 SESSION_NOT_ALLOWED + reason LEGACY_ACCESS_BLOCKED(CI-08) */
export const LEGACY_BLOCKED_MESSAGES = {
  open: '이전 방식은 막혔습니다. 「서브 관리자로 접속」을 쓰세요.',
  voicebot: '서브 관리자로 접속한 뒤 병원 화면의 음성봇 메뉴를 쓰세요.',
  accounts: '서브 관리자로 접속해 고객사 화면의 계정 관리에서 하세요.',
};

/** 막힌 옛 요청 주소 → 문구 종류: 리셀러 대행 직원 계정 경로 · 음성봇 SSO · 그 밖(대리 토큰) */
export const legacyBlockedKind = (url = '') => {
  const path = String(url).split('?')[0];
  if (path.includes('/sub-accounts')) return 'accounts';
  if (path.includes('/voicebot-token')) return 'voicebot';
  return 'open';
};

/**
 * 전환 스위치 supportAccess 가 켜졌는지(introspect `modes.supportAccess`). 'on' 이 아니거나 modes 가 없으면(옛 inapi·읽기 실패) false —
 * 그동안 고객 화면은 main 의 옛 「병원/제조사 페이지」·「챗봇 관리」를 보이고 「서브 관리자로 접속」은 숨긴다(배포 날은 지금과 같이, 10-30 결정).
 */
export const supportAccessOn = (introspect) =>
  introspect?.modes?.supportAccess === 'on';
